"use client";

import React, { createContext, ReactNode, useMemo } from "react";

/**
 * Entity context data structure
 */
export interface EntityContextData {
  entityType: string;
  entityId: string;
  entityData: Record<string, unknown>;
  entityDisplayName: string;
}

/**
 * Entity context type
 */
interface EntityContextType {
  entity: EntityContextData | null;
}

/**
 * Create the entity context
 */
const EntityContext = createContext<EntityContextType | undefined>(undefined);

/**
 * Props for EntityContextProvider
 */
interface EntityContextProviderProps {
  children: ReactNode;
  entityType: string;
  entityId: string;
  entityData: Record<string, unknown>;
  entityDisplayName: string;
}

/**
 * EntityContextProvider component
 * Provides entity context to all child components
 */
export function EntityContextProvider({
  children,
  entityType,
  entityId,
  entityData,
  entityDisplayName,
}: EntityContextProviderProps) {
  const value = useMemo<EntityContextType>(
    () => ({
      entity: {
        entityType,
        entityId,
        entityData,
        entityDisplayName,
      },
    }),
    [entityType, entityId, entityData, entityDisplayName]
  );

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}

/**
 * Hook to access entity context
 * Throws error if used outside of EntityContextProvider
 */
export function useEntityContext(): EntityContextData {
  const context = React.useContext(EntityContext);

  if (!context || !context.entity) {
    throw new Error("useEntityContext must be used within an EntityContextProvider");
  }

  return context.entity;
}

/**
 * Hook to safely access entity context (returns null if not available)
 */
export function useEntityContextOptional(): EntityContextData | null {
  const context = React.useContext(EntityContext);
  return context?.entity || null;
}
